import {
  AutoComplete,
  Button,
  Cascader,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Slider,
  Switch,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "antd/lib/form/Form";
import { useEffect, useRef, useState } from "react";
import GenericFormComponent from "../../../shared/components/generic-form/generic-form.component";
import { RootState } from "../../../shared/types/common";
import { showNotification } from "../../../shared/utils/utils";
import { Client } from "../../../core/api-client";
import { API_GET_ALL_DEPARTMENT_LIST } from "../../../shared/routes/private.routes";
import {
  createJobDescription,
  updateJobDescription,
} from "../../../core/store/reducers/candidate-screening.slice";


interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
}

const CreateEditJobDescriptionComponent = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const isInitialDistributionDoneRef = useRef(false);

  const toSkillRows = (skills: any[], defaultWeight: number) => {
    if (!Array.isArray(skills) || skills.length === 0) {
      return defaultWeight > 0 ? [{ value: "", weight: defaultWeight }] : [];
    }
    return skills.flatMap((skill) => {
      const names = String(skill?.value || skill?.name || skill?.skill || "")
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);
      if (!names.length) return [];
      const groupWeight = Number(skill?.weight ?? defaultWeight);
      const rowWeight =
        Number.isFinite(groupWeight) && groupWeight > 0
          ? Number((groupWeight / names.length).toFixed(2))
          : 0;
      return names.map((value) => ({ value, weight: rowWeight }));
    });
  };

  const toSkillPayload = (skills: any[]) => {
    if (!Array.isArray(skills)) return [];
    return skills
      .map((skill) => ({
        value: (skill?.value || "").trim(),
        weight: Number(skill?.weight ?? 0),
      }))
      .filter(
        (skill) =>
          skill.value && Number.isFinite(skill.weight) && skill.weight > 0
      );
  };

  const getSkillWeightTotal = (skills: any[] = []) =>
    skills.reduce((sum, skill) => {
      const value = (skill?.value || "").trim();
      const weight = Number(skill?.weight);
      return value && Number.isFinite(weight) && weight > 0
        ? sum + weight
        : sum;
    }, 0);

  const hasIncompleteSkillRows = (skills: any[] = []) =>
    skills.some((skill) => {
      const hasValue = Boolean((skill?.value || "").trim());
      const weight = Number(skill?.weight);
      const hasWeight = Number.isFinite(weight) && weight > 0;
      return hasValue !== hasWeight;
    });

  const hasAnySkill = (skills: any[] = []) =>
    skills.some((skill) => (skill?.value || "").trim());

  const formatWeight = (weight: number) => Number(weight.toFixed(2));

  const getTotalSkillWeight = (values: any) =>
    formatWeight(
      getSkillWeightTotal(values?.primaryRules) +
        getSkillWeightTotal(values?.secondaryRules)
    );

  const fetchDepartmentList = async () => {
    const response = await Client.get(API_GET_ALL_DEPARTMENT_LIST);
    if (response?.data?.data) {
      setDepartmentList(response?.data?.data);
    } else {
      setDepartmentList([]);
    }
  };

  useEffect(() => {
    fetchDepartmentList();
  }, []);

  useEffect(() => {
    if (selectedEntity) {
      form.setFieldsValue({
        title: selectedEntity?.title,
        departmentId: selectedEntity?.departmentId,
        status: selectedEntity?.status,
        rawText: selectedEntity?.rawText,
        weightSimilarity: selectedEntity?.weightConfig?.similarity,
        weightRule: selectedEntity?.weightConfig?.rule,
        weightAi: selectedEntity?.weightConfig?.ai,
        primaryRules: toSkillRows(selectedEntity?.primarySkills, 1),
        secondaryRules: toSkillRows(selectedEntity?.secondarySkills, 0),
      });
      isInitialDistributionDoneRef.current = true;
    } else {
      // For new entities, explicitly clear the fields, especially weights
      form.resetFields();
      isInitialDistributionDoneRef.current = false;
    }
  }, [form, selectedEntity]);

  const buildPayload = (values: any) => ({
    title: values?.title,
    departmentId: values?.departmentId,
    status: values?.status,
    rawText: values?.rawText,
    weightConfig: {
      similarity: Number(values?.weightSimilarity ?? 0),
      rule: Number(values?.weightRule ?? 0),
      ai: Number(values?.weightAi ?? 0),
    },
    primarySkills: toSkillPayload(values?.primaryRules),
    secondarySkills: toSkillPayload(values?.secondaryRules),
  });

  const onFinish = (values: any) => {
    console.log(values);
    console.log(
      values.weightSimilarity,
      values.weightRule,
      values.weightAi
    );
    const total =
      Number(values?.weightSimilarity ?? 0) +
      Number(values?.weightRule ?? 0) +
      Number(values?.weightAi ?? 0);
    // Weights blend the final score (plan.md §5). They must sum to 1.

    if (Math.abs(total - 1) > 0.001) {
      showNotification(
        "Score weights (similarity + rule + AI) must add up to 1.",
        "warning"
      );
      return;
    }

    if (Number(values?.weightRule ?? 0) > 0) {
      if (!hasAnySkill(values?.primaryRules)) {
        showNotification(
          "Primary skills are required when Rule Weight is greater than 0.",
          "warning"
        );
        return;
      }
    }

    if (
      hasIncompleteSkillRows(values?.primaryRules) ||
      hasIncompleteSkillRows(values?.secondaryRules)
    ) {
      showNotification("Each configured skill must have a skill name and weight.", "warning");
      return;
    }

    const totalSkillWeight = getTotalSkillWeight(values);
    if (totalSkillWeight > 1) {
      showNotification(
        "Primary and secondary skill weights must not exceed 1.00.",
        "warning"
      );
      return;
    }

    setSaving(true);
    const payload = buildPayload(values);
    const action = selectedEntity
      ? updateJobDescription({ id: selectedEntity?.id, body: payload })
      : createJobDescription(payload);

    dispatch(action).then((val: any) => {
      setSaving(false);
      if (val?.payload?.data) {
        showNotification(
          selectedEntity
            ? "Job Description Updated Successfully"
            : "Job Description Created Successfully",
          "success"
        );
        onSubmit();
      }
    });
  };

  const departmentOptions = departmentList.map((dept) => ({
    value: dept?.departmentId,
    label: dept?.departmentName,
  }));

  const colSpan = screenType.isPortrait ? 24 : 12;

  const formProps = {
    form: form,
    name: "create-edit-job-description",
    heading: selectedEntity
      ? "Edit Job Description"
      : "Add New Job Description",
    initialValues: {
      status: "ACTIVE",
      primaryRules: [{ value: "", weight: 1 }],
      secondaryRules: [],
      // weightSimilarity: null,
      // weightRule: null,
      // weightAi: null,
    },
    formElements: [
      {
        name: "title",
        colSpan: 24,
        label: "Job Title",
        placeholder: "e.g. Senior Backend Engineer",
        type: "Input",
        allowClear: true,
        rules: [{ required: true, message: "Please enter a job title" }],
      },
      {
        name: "departmentId",
        label: "Department",
        placeholder: "Select Department",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: departmentOptions,
      },
      {
        name: "status",
        label: "Status",
        placeholder: "Status",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
        rules: [{ required: true, message: "Please select a status" }],
      },
      {
        name: "rawText",
        colSpan: 24,
        label: "Job Description Text",
        placeholder:
          "Paste the full job description. This is parsed and embedded for screening.",
        type: "TextArea",
        rows: 8,
        showCount: true,
        maxLength: 20000,
        rules: [
          { required: true, message: "Please enter the job description text" },
        ],
      },
      {
        type: "TextLabel",
        colSpan: 24,
        name: "primarySkillsLabel",
        textlabel: "Rule Weight",
      },
      {
        type: "InfoAlert",
        colSpan: 24,
        name: "scoreWeightsHelp",
        alertType: "warning",
        message: "Rule Weight uses the skills below.",
        description: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Each skill can have its own weight.</li>
            <li>Primary Skills are mandatory when Rule Weight is greater than 0.</li>
            <li>Secondary Skills are optional.</li>
            <li>Total skill weight must not exceed 1.00.</li>
          </ul>
        ),
      },
      {
        type: "PrimaryRule",
        name: "primaryRules",
        colSpan: 24,
        
      },
      {
        type: "SecondaryRule",
        name: "secondaryRules",
        colSpan: 24,
      },
      {
        type: "TextLabel",
        colSpan: 24,
        name: "scoreWeightsHeading",
        textlabel: "Score Weights",
        tooltip:
          "These three weights decide how much each scoring component contributes to the final candidate score. The total must equal 1.00.",
      },
      {
        name: "weightRule",
        label: "Rule Weight",
        tooltip:
          "Uses the primary and secondary skills configured above. Higher rule weight means skill matches affect the final score more.",
        placeholder: "0.20",
        type: "InputNumber",
        colSpan: 8,
        min: 0,
        max: 100,
        isWeight: true,
        isDecimal: true,
       
      },
      {
        name: "weightSimilarity",
        label: "Similarity Weight",
        tooltip:
          "Compares the full job description and resume as embeddings. Higher similarity weight means overall resume/JD semantic match affects the final score more.",
        placeholder: "0.50",
        type: "InputNumber",
        colSpan: 8,
        min: 0,
        max: 100,
        isWeight: true,
        isDecimal: true,
       
      },
      {
        name: "weightAi",
        label: "AI Weight",
        tooltip:
          "Uses AI evidence scoring for the configured skills. Higher AI weight means the AI evidence score affects the final score more.",
        placeholder: "0.30",
        type: "InputNumber",
        colSpan: 8,
        min: 0,
        max: 100,
        isWeight: true,
        isDecimal: true,
        
      }
    ],
    extraButtons: [
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Cancel",
        onClick: onClose,
      },
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm", // This is handled by GenericFormComponent
        onClick: () => {
          // Reset the flag when the form is cleared
          isInitialDistributionDoneRef.current = false;
        },
      },
      {
        htmlType: "submit",
        colSpan: 5,
        loading: saving,
        submitButtonText: "Save",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFinish,
    onFinishFailed: () => {
      return;
    },
   onValuesChange: (changedValues: any, allValues: any) => {
  const changedWeightField = Object.keys(changedValues).find(
    (key) =>
      key === "weightSimilarity" ||
      key === "weightRule" ||
      key === "weightAi"
  );

  if (!changedWeightField) return;

  const toDecimal = (value: any) => {
    const num = Number(value);

    if (Number.isNaN(num) || num < 0) {
      return 0;
    }

    return num > 1 ? num / 100 : num;
  };

  const finalSim = toDecimal(allValues.weightSimilarity);
  const finalRule = toDecimal(allValues.weightRule);
  const finalAi = toDecimal(allValues.weightAi);

  const total = Number(
    (finalSim + finalRule + finalAi).toFixed(2)
  );

  if (Math.abs(total - 1) > 0.001) {
    form.setFields([
      {
        name: "weightAi",
        errors: ["Total weight must equal 1.00"],
      },
    ]);
  } else {
    form.setFields([
      {
        name: "weightAi",
        errors: [],
      },
    ]);
  }
},
  };

  

  return (
    <Drawer
      maskClosable={false}
      title={<strong>{formProps.heading}</strong>}
      className="form-drawer"
      width={
        screenType.isPortrait
          ? "90%"
          : screenType.isLg || screenType.isXl
          ? 720
          : 500
      }
      onClose={onClose}
      open={visible}
    >
      <GenericFormComponent {...formProps} />
     
    </Drawer>
  );
};

export default CreateEditJobDescriptionComponent;
